import { useEffect, useRef, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { getTranslations } from '@/i18n/translator'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, homeBreadcrumbItem } from '@/lib/structured-data'
import { sanityClient } from '@/sanity/client'
import { categoriesQuery, postsQuery } from '@/sanity/queries'
import { filterPosts } from '@/features/blog/filter-posts'
import { BlogHero } from '@/features/blog/components/blog-hero'
import { PostCard, type PostCardData } from '@/features/blog/components/post-card'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { FinalCtaSection } from '@/features/landing-page/components/final-cta-section'
import { trackListItemClick } from '@/lib/analytics/events'

interface Category {
  title: string
  slug: string
}

type Post = PostCardData & { categories?: Category[] }

interface BlogSearch {
  category?: string
  q?: string
  page?: number
}

const POSTS_PER_PAGE = 9

export const Route = createFileRoute('/$locale/blog/')({
  // Filters live in the URL so the loader (below) renders the filtered list
  // server-side — crawlers get the actual results, not an empty client shell.
  validateSearch: (search: Record<string, unknown>): BlogSearch => {
    const page = Number(search.page)
    const category = typeof search.category === 'string' ? search.category.trim() : ''
    const q = typeof search.q === 'string' ? search.q.trim() : ''
    return {
      category: category || undefined,
      q: q || undefined,
      // page 1 is the default; keep it out of the URL for a clean canonical form.
      page: Number.isInteger(page) && page > 1 ? page : undefined,
    }
  },
  loaderDeps: ({ search }) => ({ category: search.category, q: search.q, page: search.page }),
  loader: async ({ params, deps }) => {
    const [posts, categories, t] = await Promise.all([
      sanityClient.fetch<Post[]>(postsQuery(params.locale)),
      sanityClient.fetch<Category[]>(categoriesQuery(params.locale)),
      getTranslations({ locale: params.locale, namespace: 'blog' }),
    ])
    // ponytail: filter + paginate in the loader (server-rendered for SEO).
    // Fetches all posts then slices — fine at this scale; push the filter into
    // the GROQ query if the catalog ever grows to hundreds.
    const filtered = filterPosts(posts, deps.category ?? null, deps.q ?? '')
    const pageCount = Math.max(1, Math.ceil(filtered.length / POSTS_PER_PAGE))
    const page = Math.min(Math.max(deps.page ?? 1, 1), pageCount)
    const visible = filtered.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE)
    return {
      posts: visible,
      categories,
      page,
      pageCount,
      title: t('meta.title'),
      description: t('meta.description'),
    }
  },
  head: ({ loaderData, params }) =>
    pageHead({
      locale: params.locale,
      path: '/blog',
      title: loaderData?.title ?? 'Blog',
      description: loaderData?.description ?? '',
      jsonLd: breadcrumbSchema([
        homeBreadcrumbItem(params.locale),
        { name: loaderData?.title ?? 'Blog', url: `${SITE_URL}/${params.locale}/blog` },
      ]),
    }),
  component: BlogList,
})

function BlogList() {
  const { posts, categories, page, pageCount } = Route.useLoaderData()
  const { locale } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const t = useTranslations('blog')

  // Local mirror so typing feels instant; the URL (and the server loader) is
  // updated debounced. Re-sync when q changes externally (tab click, back/fwd).
  const [queryInput, setQueryInput] = useState(search.q ?? '')
  useEffect(() => {
    setQueryInput(search.q ?? '')
  }, [search.q])

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const onSearchChange = (value: string) => {
    setQueryInput(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      navigate({
        search: (prev) => ({ ...prev, q: value.trim() || undefined, page: undefined }),
        replace: true,
      })
    }, 300)
  }

  return (
    <>
      <BlogHero />

      {/* Filter bar: category tabs (crawlable links) left, search right. */}
      <div>
        <div className="mx-auto flex max-w-7xl flex-col gap-4 border-b border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto">
            {[{ title: t('all'), slug: null }, ...categories].map((c) => (
              // Real anchors (crawlable); `exact` search matching keeps only the
              // truly-current tab active — Link sets aria-current="page" for it.
              <Link
                key={c.slug ?? 'all'}
                to="/$locale/blog"
                params={{ locale }}
                search={{ category: c.slug ?? undefined, q: search.q, page: undefined }}
                activeOptions={{ exact: true }}
                className="inline-flex min-h-11 items-center rounded-md px-3 py-1.5 text-sm whitespace-nowrap text-primary transition-colors sm:min-h-0"
                activeProps={{ className: 'bg-primary/10 font-semibold' }}
                inactiveProps={{ className: 'font-medium hover:bg-primary/10' }}
              >
                {c.title}
              </Link>
            ))}
          </div>
          <SearchInput
            value={queryInput}
            onChange={onSearchChange}
            placeholder={t('searchPlaceholder')}
            className="sm:w-72"
          />
        </div>
      </div>

      <div className="py-12">
        {posts.length === 0 && (
          <p className="mx-auto max-w-7xl px-6 py-12 text-center text-muted-foreground lg:px-8">
            {t('empty')}
          </p>
        )}
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-6 gap-y-10 px-6 sm:grid-cols-2 lg:grid-cols-3 lg:px-8">
          {posts.map((post) => (
            <PostCard
              key={post.slug}
              post={post}
              locale={locale}
              onNavigate={() => trackListItemClick({ destination: `/blog/${post.slug}` })}
            />
          ))}
        </div>

        {pageCount > 1 && (
          <nav
            className="mx-auto mt-12 flex max-w-7xl flex-wrap items-center justify-center gap-1 px-6 lg:px-8"
            aria-label={t('pagination.label')}
          >
            {page > 1 ? (
              <Button asChild variant="ghost" size="icon" className="size-11 sm:size-8" aria-label={t('pagination.previous')}>
                <Link
                  to="/$locale/blog"
                  params={{ locale }}
                  search={(prev) => ({ ...prev, page: page - 1 > 1 ? page - 1 : undefined })}
                  activeOptions={{ exact: true }}
                >
                  <ChevronLeft />
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="icon" disabled className="size-11 sm:size-8" aria-label={t('pagination.previous')}>
                <ChevronLeft />
              </Button>
            )}
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <Button
                key={n}
                asChild
                variant={n === page ? 'secondary' : 'ghost'}
                size="icon"
                className="size-11 sm:size-8"
                aria-current={n === page ? 'page' : undefined}
              >
                <Link
                  to="/$locale/blog"
                  params={{ locale }}
                  search={(prev) => ({ ...prev, page: n > 1 ? n : undefined })}
                  activeOptions={{ exact: true }}
                >
                  {n}
                </Link>
              </Button>
            ))}
            {page < pageCount ? (
              <Button asChild variant="ghost" size="icon" className="size-11 sm:size-8" aria-label={t('pagination.next')}>
                <Link
                  to="/$locale/blog"
                  params={{ locale }}
                  search={(prev) => ({ ...prev, page: page + 1 })}
                  activeOptions={{ exact: true }}
                >
                  <ChevronRight />
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="icon" disabled className="size-11 sm:size-8" aria-label={t('pagination.next')}>
                <ChevronRight />
              </Button>
            )}
          </nav>
        )}
      </div>

      <FinalCtaSection className="pt-8" />
    </>
  )
}
